/**
 * 画像アップロードコンポーネント
 */

import React, { useRef } from "react";
import "./ImageUploader.css";

export interface ImageInfo {
  name: string;
  size: number;
  sizeUnit: string;
  keyName: string;
  objectName?: string; // GCS上のオブジェクト名（アップロード後）
  storagePath?: string; // GCSストレージパス（エンティティに保存用）
  previewUrl?: string; // プレビュー用のURL（ローカルまたはGCS）
  file?: File; // ローカルファイル（アップロード前）
  mediaType?: "image" | "video"; // メディアタイプ
}

interface ImageUploaderProps {
  onUploadComplete: (imageInfo: ImageInfo) => void;
  onError?: (error: string) => void;
  accept?: string;
  maxSize?: number; // 最大ファイルサイズ（バイト）
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadComplete,
  onError,
  accept = "image/*",
  maxSize = 10 * 1024 * 1024, // デフォルト10MB
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): { size: number; unit: string } => {
    if (bytes < 1024) {
      return { size: bytes, unit: "B" };
    } else if (bytes < 1024 * 1024) {
      return { size: Math.round(bytes / 1024), unit: "KB" };
    } else {
      return { size: Math.round(bytes / (1024 * 1024)), unit: "MB" };
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック
    if (file.size > maxSize) {
      const maxSizeFormatted = formatFileSize(maxSize);
      onError?.(
        `ファイルサイズが大きすぎます。最大${maxSizeFormatted.size}${maxSizeFormatted.unit}までアップロードできます。`
      );
      return;
    }

    // ファイルタイプチェック
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (accept === "image/*" && !isImage) {
      onError?.("画像ファイルを選択してください");
      return;
    }
    if (accept === "video/*" && !isVideo) {
      onError?.("動画ファイルを選択してください");
      return;
    }
    if (accept === "image/*,video/*" && !isImage && !isVideo) {
      onError?.("画像または動画ファイルを選択してください");
      return;
    }

    // ファイルサイズをフォーマット
    const { size, unit } = formatFileSize(file.size);

    // ローカルプレビューURLを生成
    const previewUrl = URL.createObjectURL(file);

    // メディアタイプを判定
    const mediaType: "image" | "video" = isVideo ? "video" : "image";

    // 画像情報を作成（アップロードは保存時に行う）
    const imageInfo: ImageInfo = {
      name: file.name,
      size: size,
      sizeUnit: unit,
      keyName: "", // ユーザーが後で設定
      file: file, // ローカルファイルを保持
      previewUrl: previewUrl, // ローカルプレビューURL
      mediaType: mediaType, // メディアタイプ
    };

    onUploadComplete(imageInfo);

    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // ボタンテキストとアイコンを決定
  const getButtonContent = () => {
    if (accept === "video/*") {
      return { icon: "🎬", text: "動画を選択" };
    } else if (accept === "image/*,video/*") {
      return { icon: "📎", text: "メディアを選択" };
    }
    return { icon: "📷", text: "画像を選択" };
  };

  const { icon, text } = getButtonContent();

  return (
    <div className="image-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      <button type="button" className="upload-button" onClick={handleClick}>
        <span className="upload-icon">{icon}</span>
        <span>{text}</span>
      </button>
    </div>
  );
};
